import { useEffect, useRef, useState, type FormEvent } from 'react'
import { getAuthType, isValidEmail } from '@/shared/auth/login'
import { openExternal, FORGOT_PASSWORD_URL } from '@/shared/auth/sso'
import { AppVersion } from '@/shared/components/AppVersion'
import logoUrl from '@/assets/ramp-logo.svg'

interface LoginScreenProps {
  onSubmit: (email: string, password: string) => Promise<void>
  onSso: (provider: 'azure' | 'saml', email: string) => Promise<void>
  error: string | null
  signingIn: boolean
}

/**
 * Two-step sign-in matching ramp-web-app's Login:
 *   1. Email -> "Next" -> getAuthType(email)
 *   2a. RAMP            -> password -> local OAuth2 password grant
 *   2b. AZURE_AD / SAML -> SSO via the Office Dialog API
 */
export function LoginScreen({ onSubmit, onSso, error, signingIn }: LoginScreenProps) {
  const [step, setStep] = useState<'email' | 'password'>('email')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [checking, setChecking] = useState(false)
  const [localError, setLocalError] = useState<string | null>(null)
  const passwordRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (step === 'password') passwordRef.current?.focus()
  }, [step])

  const back = () => {
    setStep('email')
    setPassword('')
    setLocalError(null)
  }

  const handleEmailNext = async () => {
    if (!isValidEmail(email)) {
      setLocalError('Enter a valid email address.')
      return
    }
    setChecking(true)
    setLocalError(null)
    try {
      const type = await getAuthType(email)
      if (type === 'RAMP') {
        setStep('password')
      } else if (type === 'AZURE_AD') {
        await onSso('azure', email)
      } else if (type === 'SAML') {
        await onSso('saml', email)
      } else {
        setLocalError('Unable to determine the sign-in method for this account.')
      }
    } catch (e) {
      setLocalError(e instanceof Error ? e.message : 'Could not look up this account.')
    } finally {
      setChecking(false)
    }
  }

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    if (checking || signingIn) return
    if (step === 'email') {
      handleEmailNext()
      return
    }
    if (!password) return
    onSubmit(email, password).catch(() => {
      /* error surfaced via the `error` prop */
    })
  }

  const busy = checking || signingIn
  const shownError = localError || error
  const disabled = busy || (step === 'email' ? !email : !password)

  return (
    <div className='ramp-login'>
      <div className='ramp-login-card'>
          <img className='ramp-login-logo' src={logoUrl} alt='ramp' />
          {/* First-run value proposition below the logo, required by
              AppSource policy 1100.1.5. */}
          <p className='ramp-login-tagline'>
            Scan documents against compliance checklists, apply suggested fixes as tracked changes, and draft content
            grounded in your FilingRamp filings data — right inside Word, Excel, and PowerPoint.
          </p>
          <h3 className='ramp-login-header'>Sign in to your account</h3>

          {shownError && <div className='ramp-login-error'>{shownError}</div>}

          <form onSubmit={handleSubmit}>
            {step === 'email' ? (
              <div className='ramp-login-field'>
                <span>Email</span>
                <input
                  className='ramp-login-input'
                  type='email'
                  autoComplete='username'
                  autoFocus
                  placeholder='Your email'
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={busy}
                />
              </div>
            ) : (
              <div className='ramp-login-field'>
                <span>Password</span>
                <input
                  ref={passwordRef}
                  className='ramp-login-input'
                  type='password'
                  autoComplete='current-password'
                  placeholder='Your password'
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={busy}
                />
              </div>
            )}

            <button type='submit' className='ramp-login-button' disabled={disabled}>
              {step === 'email' ? (checking ? 'Checking…' : 'Next') : signingIn ? 'Signing in…' : 'Sign In'}
            </button>

            <div className='ramp-login-actions'>
              {step === 'password' && (
                <button type='button' className='ramp-login-link' onClick={back} disabled={busy}>
                  Back
                </button>
              )}
              <button type='button' className='ramp-login-link' onClick={() => openExternal(FORGOT_PASSWORD_URL)}>
                Forgot password? Click here
              </button>
            </div>
          </form>

          {/* Account acquisition for enterprise users (AppSource policy 1100.5.7). */}
          <p className='ramp-login-signup'>
            Don&apos;t have an account? FilingRamp is provisioned for your organization — contact us at{' '}
            <a className='ramp-login-link' href='mailto:support@filingramp.com'>
              support@filingramp.com
            </a>{' '}
            to get access.
          </p>
      </div>
      <AppVersion />
    </div>
  )
}
